/**
 * WeekGrid — the #665 master person-row schedule grid (spec §3 Phase 1).
 *
 * Rows = people (active roster first, then anyone deactivated who still
 * holds shifts this week — a commitment doesn't evaporate with the login).
 * Columns = Mon–Sun. Cells = shift chips; split shifts render as multiple
 * chips in one cell. Approved time off draws a band IN the person×day cell
 * so a planner sees the absence exactly where she is about to click.
 *
 * Draft chips (published_at NULL) follow the striped-drafts convention:
 * dashed border + diagonal stripe pattern + the word "Draft" — the state is
 * carried by PATTERN and TEXT, never by color alone (DESIGN-PHILOSOPHY P6;
 * the syringe-badge lesson). Published chips are solid.
 *
 * Write affordances are the CALLER's decision via `canWrite` (#712): for
 * read roles the chips are inert <span>s and the per-cell add button does
 * not exist — never an affordance the server will 403 (P5).
 *
 * Principles served: P1 (one glance = who works when, per person), P2 (the
 * empty cell IS the create affordance), P3 (names and hours, no jargon),
 * P5 (drafts never masquerade as commitments), P7 (44px touch targets on
 * chips and add buttons).
 */
import { useMemo } from 'react';
import {
  TIME_OFF_TYPE_LABELS,
  type StaffShift,
  type TimeOffRequest,
  type TimeOffType,
} from '../../lib/api.js';
import { formatTimeRange, isDayWithin } from '../../lib/schedule-dates.js';
import { JobFunctionChip, jobFunctionLabel } from './JobFunctionChip.js';

/** NULL = draft. A MISSING field means a legacy serializer row — treat as
 *  published, never as a draft (absence of evidence is not draftness). */
export function isDraftShift(s: StaffShift): boolean {
  return s.published_at === null;
}

export type WeekGridPerson = {
  readonly staffId: string;
  readonly name: string;
  /** false = deactivated but still scheduled this week (row kept, no adds). */
  readonly isActive: boolean;
};

export interface WeekGridProps {
  readonly weekDates: ReadonlyArray<{ iso: string; label: string }>;
  readonly people: ReadonlyArray<WeekGridPerson>;
  readonly shifts: ReadonlyArray<StaffShift>;
  /** APPROVED time off (bands). The unavailable-state banner is the page's. */
  readonly timeOff: ReadonlyArray<TimeOffRequest>;
  readonly canWrite: boolean;
  readonly onAddShift?: (staffId: string, iso: string) => void;
  readonly onEditShift?: (shift: StaffShift) => void;
}

function timeOffTypeLabel(type: string): string {
  return TIME_OFF_TYPE_LABELS[type as TimeOffType] ?? 'Time off';
}

function dayNumberOf(iso: string): string {
  return String(new Date(`${iso}T00:00:00`).getDate());
}

export function WeekGrid({
  weekDates,
  people,
  shifts,
  timeOff,
  canWrite,
  onAddShift,
  onEditShift,
}: WeekGridProps): JSX.Element {
  // person → day → shifts, sorted by start time (split shifts stay in
  // chronological order inside their cell).
  const byPersonDay = useMemo(() => {
    const map = new Map<string, Map<string, StaffShift[]>>();
    for (const s of shifts) {
      const days = map.get(s.staff_user_id) ?? new Map<string, StaffShift[]>();
      const list = days.get(s.date) ?? [];
      list.push(s);
      days.set(s.date, list);
      map.set(s.staff_user_id, days);
    }
    for (const days of map.values()) {
      for (const list of days.values()) {
        list.sort((a, b) => a.start_time.localeCompare(b.start_time));
      }
    }
    return map;
  }, [shifts]);

  const awayOf = (staffId: string, iso: string): TimeOffRequest[] =>
    timeOff.filter(
      (r) =>
        r.staff_user_id === staffId &&
        isDayWithin(iso, r.start_date, r.end_date),
    );

  return (
    <div className="ferro-weekgrid-wrap">
      <table className="ferro-weekgrid" data-testid="week-person-grid">
        <thead>
          <tr>
            <th scope="col" className="ferro-weekgrid__corner">
              <span className="sr-only">Staff member</span>
            </th>
            {weekDates.map(({ iso, label }) => (
              <th key={iso} scope="col" className="ferro-weekgrid__dayhead">
                <span className="ferro-weekgrid__daylabel">{label}</span>{' '}
                <span className="ferro-weekgrid__daynum">
                  {dayNumberOf(iso)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {people.map((person) => (
            <tr
              key={person.staffId}
              className="ferro-weekgrid__row"
              data-testid="grid-person-row"
            >
              <th scope="row" className="ferro-weekgrid__person">
                <span className="ferro-weekgrid__person-name">
                  {person.name}
                </span>
                {!person.isActive ? (
                  <span className="ferro-weekgrid__person-inactive">
                    inactive
                  </span>
                ) : null}
              </th>
              {weekDates.map(({ iso }) => {
                const cellShifts =
                  byPersonDay.get(person.staffId)?.get(iso) ?? [];
                const away = awayOf(person.staffId, iso);
                return (
                  <td
                    key={iso}
                    className="ferro-weekgrid__cell"
                    data-testid="grid-cell"
                  >
                    {away.map((r) => (
                      <div
                        key={r.id}
                        className="ferro-timeoff-band ferro-timeoff-band--approved ferro-weekgrid__timeoff"
                        data-testid="week-timeoff-band"
                      >
                        {r.staff_name} — {timeOffTypeLabel(r.type)}
                      </div>
                    ))}
                    {cellShifts.map((s) => {
                      const draft = isDraftShift(s);
                      const chipLabel = `${formatTimeRange(
                        s.start_time,
                        s.end_time,
                      )}, ${jobFunctionLabel(String(s.role))}${
                        draft ? ' (draft)' : ''
                      } — ${person.name}`;
                      const chipClass = `ferro-shift-chip${
                        draft ? ' ferro-shift-chip--draft' : ''
                      }`;
                      const body = (
                        <>
                          <span className="ferro-shift-chip__time">
                            {formatTimeRange(s.start_time, s.end_time)}
                          </span>
                          <span className="ferro-shift-chip__meta">
                            <JobFunctionChip role={String(s.role)} />
                            {draft ? (
                              <span className="ferro-shift-chip__draftmark">
                                Draft
                              </span>
                            ) : null}
                          </span>
                        </>
                      );
                      // Writers get a button into the edit sheet; read roles
                      // get an inert chip — same information, no dead door.
                      return canWrite ? (
                        <button
                          key={s.id}
                          type="button"
                          className={chipClass}
                          data-testid="shift-chip"
                          data-draft={draft ? 'true' : 'false'}
                          aria-label={`Edit shift: ${chipLabel}`}
                          onClick={() => onEditShift?.(s)}
                        >
                          {body}
                        </button>
                      ) : (
                        // No aria-label here: a generic <span> has no role,
                        // so AT ignores the attribute — the visible time +
                        // function + Draft text IS the accessible content
                        // (review minor f).
                        <span
                          key={s.id}
                          className={chipClass}
                          data-testid="shift-chip"
                          data-draft={draft ? 'true' : 'false'}
                        >
                          {body}
                        </span>
                      );
                    })}
                    {/* The empty (or not-yet-full — split shifts are legal)
                        cell is the person-first create affordance. Only for
                        ACTIVE people: the server refuses shifts for
                        deactivated staff, so no button is offered (P5). */}
                    {canWrite && person.isActive ? (
                      <button
                        type="button"
                        className="ferro-weekgrid__add"
                        data-testid="grid-add-shift"
                        // NOT "Add shift …": the toolbar button owns that
                        // accessible name, and two controls answering the
                        // same name is ambiguity for switch/voice users
                        // (and for every /add shift/i query in the suites).
                        aria-label={`Schedule ${person.name} on ${iso}`}
                        onClick={() => onAddShift?.(person.staffId, iso)}
                      >
                        +
                      </button>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
